import { alpha, createTheme } from '@mui/material/styles';

import { AccordionTheme } from './components/Accordion';
import { Baseline } from './components/Baseline';

export const theme = createTheme({
    palette: {
        background: {
            default: '#e6eaee',
        },
        common: {
            black: '#000',
            white: '#fff',
        },
        primary: {
            main: '#3F6EB7',
            100: 'rgba(63, 110, 183, 0.10)',
            400: '#5492F4',
            500: '#5CA1FF',
        },
        secondary: {
            main: '#267C9B',
        },
        success: {
            main: '#61C2A2',
            light: '#1CC64B24',
        },
        warning: {
            light: '#FFF6E6',
            main: '#F0C187',
            700: '#E68A00',
            dark: '#B76500',
        },
        error: {
            light: '#FF0000',
            main: '#BF1B28',
        },
        blue: {
            500: '#2196F3',
            main: '#2196F3',
        },
        grey: {
            100: '#F8F8F8',
            200: '#F5F5F5',
            300: '#E9E8E84D',
            400: '#B7B7B7',
            500: '#E9E8E8',
            600: '#787878',
        },
        metrics: {
            positive: '#4BA054',
            negative: '#BF1B28',
            base: '#3F6EB7',
            warning: '#EA7D32',
        },
        text: {
            disabled: '#363636',
        },
    },
    typography: {
        fontFamily: `Open Sans, Poppins, sans-serif`,
        button: {
            fontSize: '15px',
            letterSpacing: '0.46px',
            textTransform: 'capitalize',
        },
        openSans: {
            h2: {
                fontFamily: 'Open Sans',
                fontSize: '24px',
                lineHeight: '32px',
                fontWeight: 600,
            },
            h3: {
                fontFamily: 'Open Sans',
                fontSize: '20px',
                lineHeight: '24px',
                fontWeight: 600,
            },
            h5: {
                fontFamily: 'Open Sans',
                fontSize: '24px',
                lineHeight: '32px',
                fontWeight: 400,
            },
            h6: {
                fontFamily: 'Open Sans',
                fontSize: '20px',
                lineHeight: '24px',
                letterSpacing: '0.15px',
            },
            subtitle1: {
                fontFamily: 'Open Sans',
                fontSize: '16px',
                lineHeight: '20px',
                letterSpacing: '0.15px',
            },
            subtitle2: {
                fontFamily: 'Open Sans',
                fontSize: '14px',
                lineHeight: '24px',
                fontWeight: 600,
                letterSpacing: '0.1px',
            },
            subtitle1Medium: {
                fontFamily: 'Open Sans',
                fontSize: '16px',
                lineHeight: '20px',
                fontWeight: 600,
                letterSpacing: '0.15px',
            },
            body1: {
                fontFamily: 'Open Sans',
                fontSize: '16px',
                lineHeight: '24px',
                fontWeight: 400,
            },
            body2: {
                fontFamily: 'Open Sans',
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0.25px',
            },
            body2Medium: {
                fontFamily: 'Open Sans',
                fontSize: '14px',
                lineHeight: '20px',
                fontWeight: 600,
                letterSpacing: '0.1px',
            },
            button: {
                fontFamily: 'Open Sans',
                fontSize: '15px',
                lineHeight: '26px',
                fontWeight: 600,
                letterSpacing: '0.46px',
            },
            caption: {
                fontFamily: 'Open Sans',
                fontSize: '12px',
                lineHeight: '16px',
                fontWeight: 400,
                letterSpacing: '0.4px',
            },
            captionMedium: {
                fontFamily: 'Open Sans',
                fontSize: '12px',
                lineHeight: '16px',
                fontWeight: 600,
                letterSpacing: '0.2px',
            },
        },
        poppins: {
            h2: {
                fontFamily: 'Poppins',
                fontSize: '24px',
                lineHeight: '32px',
                fontWeight: 600,
            },
            h3: {
                fontFamily: 'Poppins',
                fontSize: '20px',
                lineHeight: '24px',
                fontWeight: 600,
            },
            h4: {
                fontFamily: 'Poppins',
                fontSize: '17px',
                lineHeight: '24px',
                fontWeight: 600,
            },
            h5: {
                fontFamily: 'Poppins',
                fontSize: '24px',
                lineHeight: '32px',
                fontWeight: 600,
            },
            h6: {
                fontFamily: 'Poppins',
                fontSize: '20px',
                lineHeight: '32px',
                fontWeight: 600,
            },
            subtitle1: {
                fontFamily: 'Poppins',
                fontSize: '16px',
                lineHeight: '20px',
                letterSpacing: '0.15px',
                fontWeight: 600,
            },
            subtitle2: {
                fontFamily: 'Poppins',
                fontSize: '14px',
                lineHeight: '24px',
                letterSpacing: '0.1px',
                fontWeight: 600,
            },
            body1: {
                fontFamily: 'Poppins',
                fontSize: '17px',
                fontWeight: 600,
                lineHeight: '20px',
                letterSpacing: '0.25px',
            },
            body2: {
                fontFamily: 'Poppins',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '20px',
                letterSpacing: '0.25px',
            },
        },
    },
    components: {
        MuiCssBaseline: Baseline,
        MuiCheckbox: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&.Mui-checked': {
                        color: theme.palette.primary.main,
                    },
                    '&.Mui-disabled': {
                        color: alpha(theme.palette.common?.black, 0.2),
                    },
                }),
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&.Mui-disabled': {
                        '& .MuiFormControlLabel-label': {
                            color: alpha(theme.palette.common?.black, 0.2),
                        },
                    },
                }),
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    padding: '8px 22px',
                    fontSize: '15px',
                    letterSpacing: '0.46px',
                    fontWeight: 600,
                    textTransform: 'inherit',
                },
            },
            variants: [
                {
                    props: { size: 'small' },
                    style: {
                        height: '30px',
                        padding: '8px 10px',
                    },
                },
            ],
        },
        MuiFormLabel: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&.Mui-disabled': {
                        color: alpha(theme.palette.common?.black, 0.6),
                        TextFillColor: alpha(theme.palette.common?.black, 0.6),
                    },
                }),
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&.Mui-disabled>input': {
                        backgroundColor: '#F8F8F8',
                        color: alpha(theme.palette.common?.black, 0.8),
                        TextFillColor: alpha(theme.palette.common?.black, 0.8),
                        WebkitTextFillColor: alpha(
                            theme.palette.common?.black,
                            0.8,
                        ),
                    },
                    '&.Mui-disabled fieldset.MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.common?.black, 0.1),
                    },
                }),
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                },
            },
        },
        ...AccordionTheme,
    },
    breakpoints: {
        values: {
            mobile: 0,
            tablet: 768,
            laptop: 1024,
            desktop: 1200,
        },
    },
});
